import type {
  PortalCapabilityPermission,
  PortalRoleAccessSnapshot,
  ReplacePortalRoleAccessResponse,
  Role,
} from "@whitehaven/permissions";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  permissionForRoleAccessCell,
  roleAccessSections,
  sameRoleAccessPermissions,
  setRoleAccessPermission,
} from "./role-access-model";

interface RoleAccessEditorProps {
  readonly snapshot: PortalRoleAccessSnapshot;
  readonly canManage: boolean;
  readonly onSave: (
    role: Role,
    permissions: readonly PortalCapabilityPermission[],
    expectedRevision: number,
  ) => Promise<ReplacePortalRoleAccessResponse>;
  readonly onReload: () => void;
}

function accessCountLabel(count: number) {
  return `${count} grant${count === 1 ? "" : "s"}`;
}

export function RoleAccessEditor({
  snapshot,
  canManage,
  onSave,
  onReload,
}: RoleAccessEditorProps) {
  const [selectedRoleKey, setSelectedRoleKey] = useState<Role>(
    snapshot.actorRole,
  );
  const selectedRole =
    snapshot.roles.find(({ key }) => key === selectedRoleKey) ??
    snapshot.roles[0];
  const [permissions, setPermissions] = useState<
    readonly PortalCapabilityPermission[]
  >(selectedRole?.permissions ?? []);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{
    readonly tone: "success" | "error";
    readonly message: string;
  } | null>(null);

  useEffect(() => {
    if (!selectedRole) return;
    setPermissions(selectedRole.permissions);
    setNotice(null);
  }, [selectedRole?.key, selectedRole?.revision, selectedRole?.permissions]);

  const granted = useMemo(() => new Set(permissions), [permissions]);
  const sections = useMemo(() => roleAccessSections(snapshot), [snapshot]);
  const actions = snapshot.areas[0]?.actions ?? [];
  const dirty = selectedRole
    ? !sameRoleAccessPermissions(permissions, selectedRole.permissions)
    : false;
  const editable = Boolean(canManage && selectedRole?.isActive && !saving);

  if (!selectedRole) {
    return (
      <div className="role-access__empty" role="status">
        No recognized roles are available for this organization.
      </div>
    );
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editable || !dirty) return;

    setSaving(true);
    setNotice(null);
    try {
      const result = await onSave(
        selectedRole.key,
        permissions,
        selectedRole.revision,
      );
      setPermissions(result.role.permissions);
      setNotice({
        tone: "success",
        message: `${result.role.label} access was saved. Your secure session is refreshing.`,
      });
    } catch (caught: unknown) {
      setNotice({
        tone: "error",
        message:
          caught instanceof Error
            ? caught.message
            : "Role access could not be saved.",
      });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setPermissions(selectedRole.permissions);
    setNotice(null);
  };

  return (
    <form className="role-access" onSubmit={submit}>
      <header className="role-access__hero">
        <div className="role-access__hero-copy">
          <p className="role-access__eyebrow">People · Roles · Capabilities</p>
          <h1 id="portal-title" data-route-focus tabIndex={-1}>
            Role access
          </h1>
          <p>
            Give each team role exactly the portal actions it needs. Every
            checkbox is independent, and the API enforces the same boundary.
          </p>
        </div>
        <dl
          className="role-access__summary"
          aria-label="Access catalog summary"
        >
          <div>
            <dt>Portal areas</dt>
            <dd>{snapshot.areas.length}</dd>
          </div>
          <div>
            <dt>Actions per area</dt>
            <dd>{actions.length}</dd>
          </div>
          <div>
            <dt>Organization</dt>
            <dd title={snapshot.organizationId}>Current site</dd>
          </div>
        </dl>
      </header>

      <section
        className="role-access__role-panel"
        aria-labelledby="role-access-role-label"
      >
        <div>
          <label id="role-access-role-label" htmlFor="role-access-role">
            Configure a role
          </label>
          <select
            id="role-access-role"
            value={selectedRole.key}
            disabled={saving}
            onChange={(event) => setSelectedRoleKey(event.target.value as Role)}
          >
            {snapshot.roles.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label}
                {role.isActive ? "" : " — inactive"}
              </option>
            ))}
          </select>
        </div>
        <div className="role-access__role-status">
          <span
            className={`role-access__status role-access__status--${
              selectedRole.isActive ? "active" : "inactive"
            }`}
          >
            {selectedRole.isActive ? "Active role" : "Inactive role"}
          </span>
          <strong>{accessCountLabel(permissions.length)}</strong>
          <small>Revision {selectedRole.revision}</small>
        </div>
      </section>

      {!canManage && (
        <div className="role-access__notice" role="note">
          <strong>View-only access</strong>
          <span>
            You can review this matrix, but your role cannot change or save
            grants.
          </span>
        </div>
      )}
      {!selectedRole.isActive && (
        <div
          className="role-access__notice role-access__notice--inactive"
          role="note"
        >
          <strong>This role is inactive</strong>
          <span>
            Its current grants remain visible, but inactive roles cannot be
            changed.
          </span>
        </div>
      )}

      <div className="role-access__legend" aria-label="Capability guidance">
        {actions.map((action) => (
          <div key={action.key}>
            <strong>{action.label}</strong>
            <span>
              {action.key === "view" &&
                "Shows the area in navigation and allows browsing."}
              {action.key === "create" &&
                "Allows creating new records or sections."}
              {action.key === "write" &&
                "Allows editing and saving existing records."}
              {action.key === "publish" &&
                "Allows approval and publication actions."}
              {action.key === "manage" &&
                "Allows administrative and destructive actions."}
            </span>
          </div>
        ))}
      </div>

      <div
        className="role-access__matrix"
        role="region"
        aria-label={`${selectedRole.label} capability matrix`}
        tabIndex={0}
      >
        <table>
          <thead>
            <tr>
              <th scope="col">Portal area</th>
              {actions.map((action) => (
                <th key={action.key} scope="col">
                  {action.label}
                </th>
              ))}
            </tr>
          </thead>
          {sections.map((section) => (
            <tbody key={section.label}>
              <tr className="role-access__section-row">
                <th colSpan={actions.length + 1} scope="rowgroup">
                  {section.label}
                  <span>{section.areas.length} areas</span>
                </th>
              </tr>
              {section.areas.map((area) => (
                <tr key={area.key}>
                  <th scope="row">
                    <span>{area.label}</span>
                    <small>{area.key}</small>
                  </th>
                  {area.actions.map((action) => {
                    const permission = permissionForRoleAccessCell(
                      area.key,
                      action.key,
                    );
                    return (
                      <td key={action.key}>
                        <label className="role-access__check">
                          <input
                            type="checkbox"
                            checked={granted.has(permission)}
                            disabled={!editable}
                            aria-label={`${selectedRole.label}: ${area.label} — ${action.label}`}
                            onChange={(event) =>
                              setPermissions((current) =>
                                setRoleAccessPermission(
                                  current,
                                  permission,
                                  event.target.checked,
                                ),
                              )
                            }
                          />
                          <span aria-hidden="true" />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <div className="role-access__save-bar">
        <div>
          <strong>
            {dirty ? "Unsaved changes" : "Role access is up to date"}
          </strong>
          <span>
            View controls navigation only; no action grant implies another.
          </span>
        </div>
        <div className="role-access__save-actions">
          <button
            className="role-access__button role-access__button--secondary"
            type="button"
            disabled={!dirty || saving}
            onClick={discard}
          >
            Discard changes
          </button>
          <button
            className="role-access__button role-access__button--primary"
            type="submit"
            disabled={!editable || !dirty}
          >
            {saving ? "Saving securely…" : `Save ${selectedRole.label}`}
          </button>
        </div>
      </div>

      {notice && (
        <div
          className={`role-access__result role-access__result--${notice.tone}`}
          role={notice.tone === "error" ? "alert" : "status"}
        >
          <span>{notice.message}</span>
          {notice.tone === "error" && (
            <button type="button" onClick={onReload}>
              Reload current access
            </button>
          )}
        </div>
      )}
    </form>
  );
}
